import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Briefcase, MapPin, Clock, ArrowRight, Heart, X, Upload, Send, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import apiClient from '../services/api';

const CareersPage = () => {
  const { t } = useLanguage();
  const [selectedJob, setSelectedJob] = useState(null);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', resume: null, coverLetter: '' });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const jobs = [
    { title: 'Senior Full Stack Developer', location: 'Remote', type: 'Full-time', department: 'Engineering' },
    { title: 'Product Designer', location: 'New York, NY', type: 'Full-time', department: 'Design' },
    { title: 'Customer Success Manager', location: 'Remote', type: 'Full-time', department: 'Support' },
    { title: 'Marketing Manager', location: 'San Francisco, CA', type: 'Full-time', department: 'Marketing' },
  ];

  const handleApply = (job) => {
    setSelectedJob(job);
    setSubmitted(false);
    setError('');
    setFormData({ name: '', email: '', phone: '', resume: null, coverLetter: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Create FormData for file upload
      const submitData = new FormData();
      submitData.append('job_title', selectedJob.title);
      submitData.append('name', formData.name);
      submitData.append('email', formData.email);
      if (formData.phone) submitData.append('phone', formData.phone);
      if (formData.coverLetter) submitData.append('cover_letter', formData.coverLetter);
      submitData.append('resume', formData.resume);

      await apiClient.post('/careers/apply', submitData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setSubmitted(true);
    } catch (err) {
      console.error('Application error:', err);
      setError(err.response?.data?.detail || 'Failed to submit application. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const closeModal = () => {
    setSelectedJob(null);
    setSubmitted(false);
    setError('');
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Heart className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-4xl font-bold text-foreground mb-4">{t('joinOurTeamTitle')}</h1>
            <p className="text-xl text-muted-foreground">{t('careersSubtitle')}</p>
          </div>

          <div className="bg-gradient-to-r from-emerald-500/10 to-teal-500/10 rounded-2xl p-8 mb-12">
            <h2 className="text-xl font-semibold text-foreground mb-4">{t('whyWorkWithUs')}</h2>
            <div className="grid md:grid-cols-2 gap-4 text-muted-foreground">
              <div>• {t('missionDrivenDesc')}</div>
              <div>• Competitive salary & equity</div>
              <div>• {t('remoteFirstDesc')}</div>
              <div>• Unlimited PTO</div>
              <div>• Health, dental & vision insurance</div>
              <div>• Learning & development budget</div>
            </div>
          </div>

          <h2 className="text-2xl font-bold text-foreground mb-6">{t('openPositionsTitle')}</h2>
          <div className="space-y-4">
            {jobs.map((job, index) => (
              <div key={index} className="bg-card border border-border rounded-xl p-6 hover:shadow-lg transition-shadow">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-foreground mb-2">{job.title}</h3>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1"><Briefcase className="w-4 h-4" /> {job.department}</span>
                      <span className="flex items-center gap-1"><MapPin className="w-4 h-4" /> {job.location}</span>
                      <span className="flex items-center gap-1"><Clock className="w-4 h-4" /> {job.type}</span>
                    </div>
                  </div>
                  <Button variant="outline" onClick={() => handleApply(job)}>Apply <ArrowRight className="w-4 h-4 ml-2" /></Button>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12 p-8 bg-card border border-border rounded-xl">
            <p className="text-muted-foreground mb-4">{"Don't see a position that fits? Send your resume to"}</p>
            <a href="mailto:careers@fundthehope.com" className="text-emerald-500 hover:underline text-lg font-medium">careers@fundthehope.com</a>
          </div>
        </div>

        {/* Application Modal */}
        {selectedJob && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-card border border-border rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-border flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-bold text-foreground">Apply for Position</h3>
                  <p className="text-sm text-muted-foreground">{selectedJob.title}</p>
                </div>
                <button onClick={closeModal} className="p-2 hover:bg-secondary rounded-full transition-colors">
                  <X className="w-5 h-5 text-muted-foreground" />
                </button>
              </div>

              {!submitted ? (
                <form onSubmit={handleSubmit} className="p-6 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Full Name *</label>
                    <Input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="John Doe"
                      className="border-2 border-emerald-500/40"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Email *</label>
                    <Input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="john@example.com"
                      className="border-2 border-emerald-500/40"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Phone</label>
                    <Input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="+1 (555) 123-4567"
                      className="border-2 border-emerald-500/40"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Resume/CV *</label>
                    <div className="border-2 border-dashed border-emerald-500/40 rounded-lg p-4 text-center hover:bg-secondary/50 transition-colors cursor-pointer">
                      <input
                        type="file"
                        accept=".pdf,.doc,.docx"
                        onChange={(e) => setFormData({ ...formData, resume: e.target.files[0] })}
                        className="hidden"
                        id="resume-upload"
                        required
                      />
                      <label htmlFor="resume-upload" className="cursor-pointer">
                        <Upload className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                        {formData.resume ? (
                          <p className="text-sm text-emerald-500">{formData.resume.name}</p>
                        ) : (
                          <p className="text-sm text-muted-foreground">Click to upload PDF, DOC, or DOCX</p>
                        )}
                      </label>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Cover Letter</label>
                    <textarea
                      value={formData.coverLetter}
                      onChange={(e) => setFormData({ ...formData, coverLetter: e.target.value })}
                      placeholder="Tell us why you're interested in this role..."
                      rows={4}
                      className="w-full px-3 py-2 bg-background border-2 border-emerald-500/40 rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    />
                  </div>
                  {error && (
                    <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-500 text-sm">
                      {error}
                    </div>
                  )}
                  <Button 
                    type="submit" 
                    disabled={loading}
                    className="w-full bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white"
                  >
                    {loading ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...</>
                    ) : (
                      <><Send className="w-4 h-4 mr-2" /> Submit Application</>
                    )}
                  </Button>
                </form>
              ) : (
                <div className="p-8 text-center">
                  <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Heart className="w-8 h-8 text-green-500" />
                  </div>
                  <h4 className="text-xl font-bold text-foreground mb-2">Application Submitted!</h4>
                  <p className="text-muted-foreground mb-6">
                    {"Thank you for applying to "}{selectedJob.title}{". We'll review your application and get back to you within 5-7 business days."}
                  </p>
                  <Button onClick={closeModal} variant="outline">Close</Button>
                </div>
              )}
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default CareersPage;
